import React, { useState, useEffect } from "react";
import Modal from "./Modal";
import SearchableSelect from "./SearchableSelect";
import {
  clusterApi,
  CreateClusterPayload,
  CreateClusterResponse,
  LocationItem,
  LanguageItem,
  ApiError,
} from "../services/api";
import { useClient } from "../contexts/ClientContext";
import "./AddAnchorKeywordModal.css";
import { useAuth } from "../contexts/AuthContext";
import { REF_DATA_VERSION } from "../config/constants";
import { ClusterOption } from "./TypeaheadSelect";
import { MyLocalStorage, VersionedCache } from "../utils/storage";

const REF_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

interface AddAnchorKeywordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onClusterCreated: (response: CreateClusterResponse) => void;
  clusterOptions: ClusterOption[];
}

// Helper function to get default max keywords based on subscription plan
const getDefaultMaxKeywords = (planName?: string | null): number => {
  if (planName === "Basic") {
    return 15;
  }
  // Pro plan or any other plan (including null/undefined) defaults to 20
  return 20;
};

const AddAnchorKeywordModal: React.FC<AddAnchorKeywordModalProps> = ({
  isOpen,
  onClose,
  onClusterCreated,
  clusterOptions,
}) => {
  const { currentClient } = useClient();
  const { tenantFeatures } = useAuth();
  const defaultMaxKeywords = getDefaultMaxKeywords(tenantFeatures.subscription_plan_name).toString();
  
  const [keyword, setKeyword] = useState("");
  const [temperature, setTemperature] = useState<string>("");
  const [maxKeywords, setMaxKeywords] = useState<string>(defaultMaxKeywords.toString());
  const [locationCode, setLocationCode] = useState<number>(2840);
  const [languageCode, setLanguageCode] = useState<string>("en");
  const [locations, setLocations] = useState<LocationItem[]>([]);
  const [languages, setLanguages] = useState<LanguageItem[]>([]);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    // Load locations from cache or API
    const cachedLocationsData = MyLocalStorage.ref_locations.get();
    if (
      cachedLocationsData &&
      cachedLocationsData.version === REF_DATA_VERSION &&
      Date.now() - cachedLocationsData.timestamp <= REF_CACHE_TTL_MS
    ) {
      setLocations(cachedLocationsData.data);
    } else {
      clusterApi.getLocations().then((data) => {
        setLocations(data);
        MyLocalStorage.ref_locations.set({
          version: REF_DATA_VERSION,
          timestamp: Date.now(),
          data,
        });
      }).catch(() => {});
    }

    // Load languages from cache or API
    const cachedLanguagesData = MyLocalStorage.ref_languages.get();
    if (
      cachedLanguagesData &&
      cachedLanguagesData.version === REF_DATA_VERSION &&
      Date.now() - cachedLanguagesData.timestamp <= REF_CACHE_TTL_MS
    ) {
      setLanguages(cachedLanguagesData.data);
    } else {
      clusterApi.getLanguages().then((data) => {
        setLanguages(data);
        MyLocalStorage.ref_languages.set({
          version: REF_DATA_VERSION,
          timestamp: Date.now(),
          data,
        });
      }).catch(() => {});
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!keyword.trim()) {
      setError("Please enter a keyword");
      return;
    }

    const duplicateCluster = clusterOptions.find((item) => {
      const [kw, langCode, locCode] = item.value.split("||");
      return (
        kw === keyword.trim()
        && langCode === languageCode
        && +locCode === locationCode
      )
    })
    if (duplicateCluster) {
      setError(`A cluster with that anchor keyword (${duplicateCluster.keyword}), country (${duplicateCluster.countryIsoCode ?? "-"}), and language (${duplicateCluster.languageCode.toUpperCase()}) had already been made! `);
      return;
    }

    // Validate temperature if provided
    if (
      temperature &&
      (isNaN(Number(temperature)) ||
        Number(temperature) < 0 ||
        Number(temperature) > 1)
    ) {
      setError("Temperature must be a number between 0 and 1");
      return;
    }

    // Validate max keywords if provided
    if (
      maxKeywords &&
      (isNaN(Number(maxKeywords)) ||
        Number(maxKeywords) < 1 ||
        !Number.isInteger(Number(maxKeywords)))
    ) {
      setError("Max keywords must be a positive integer");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const payload: CreateClusterPayload = {
        anchor_keyword: keyword.trim(),
        // Use default values if fields are empty
        temperature: temperature ? Number(temperature) : 0.7,
        max_number_of_keywords: maxKeywords ? Number(maxKeywords) : Number(defaultMaxKeywords),
        location_code: locationCode,
        language_code: languageCode,
      };

      const response = await clusterApi.createCluster(payload);

      // Call the success handler with the response
      onClusterCreated(response);

      // Delay the close to prevent modal flashing during transition
      setTimeout(() => {
        handleClose();
      }, 50);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(`API Error: ${err.message}`);
      } else {
        setError("Failed to create cluster. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setKeyword("");
    setTemperature("");
    setMaxKeywords(defaultMaxKeywords);
    setLocationCode(2840);
    setLanguageCode("en");
    setError("");
    setIsLoading(false);
    onClose();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(e.target.value);
    if (error) setError(""); // Clear error when user starts typing
  };

  const handleTemperatureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTemperature(e.target.value);
    if (error) setError("");
  };

  const handleMaxKeywordsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMaxKeywords(e.target.value);
    if (error) setError("");
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Add Anchor Keyword">
      <form onSubmit={handleSubmit} className="add-keyword-form">
        <div className="form-group">
          <label htmlFor="keyword-input" className="form-label">
            Anchor Keyword *
          </label>
          <input
            id="keyword-input"
            type="text"
            value={keyword}
            onChange={handleInputChange}
            placeholder="Enter your anchor keyword..."
            className={`form-input ${error ? "form-input-error" : ""}`}
            disabled={isLoading}
            autoFocus
          />
        </div>

        <div className="form-group">
          <label htmlFor="temperature-input" className="form-label">
            Temperature (Optional)
          </label>
          <input
            id="temperature-input"
            type="number"
            step="0.1"
            min="0"
            max="1"
            value={temperature}
            onChange={handleTemperatureChange}
            placeholder="0.7 (default)"
            className="form-input"
            disabled={isLoading}
          />
          <span className="field-hint">
            Controls creativity (0.0 = conservative, 1.0 = creative). Default:
            0.7
          </span>
        </div>

        <div className="form-group">
          <label htmlFor="max-keywords-input" className="form-label">
            Max Keywords in Cluster (Optional)
          </label>
          <input
            id="max-keywords-input"
            type="number"
            min="1"
            value={maxKeywords}
            onChange={handleMaxKeywordsChange}
            className="form-input"
            disabled={isLoading}
          />
          <span className="field-hint">
            Number of keywords to be generated initially. You can add more keywords later.
          </span>
        </div>

        <div className="form-group">
          <label htmlFor="location-autocomplete" className="form-label">
            Country *
          </label>
          <SearchableSelect
            id="location-autocomplete"
            options={locations}
            getOptionLabel={(option) => option.location_name}
            value={locations.find((l) => l.location_code === locationCode) ?? null}
            onChange={(newValue) => setLocationCode(newValue?.location_code ?? 2840)}
            isOptionEqualToValue={(option, value) => option.location_code === value.location_code}
            placeholder="Search country..."
            disabled={isLoading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="language-autocomplete" className="form-label">
            Language *
          </label>
          <SearchableSelect
            id="language-autocomplete"
            options={languages}
            getOptionLabel={(option) => option.language_name}
            value={languages.find((l) => l.language_code === languageCode) ?? null}
            onChange={(newValue) => setLanguageCode(newValue?.language_code ?? "en")}
            isOptionEqualToValue={(option, value) => option.language_code === value.language_code}
            placeholder="Search language..."
            disabled={isLoading}
          />
        </div>

        {error && (
          <div className="error-message">
            {error}
            {(error.toLowerCase().includes("plan limit") || error.toLowerCase().includes("limit reached")) && (
              <>
                <br />
                <a
                  href="/admin?tab=billing"
                  className="upgrade-plan-link"
                >
                  Upgrade your plan for more clusters.
                </a>
              </>
            )}
          </div>
        )}

        <div className="form-actions">
          <button
            type="button"
            onClick={handleClose}
            className="btn btn-secondary"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isLoading || !keyword.trim()}
          >
            {isLoading ? (
              <>
                <span className="loading-spinner-button"></span>
                Creating Cluster...
              </>
            ) : (
              "Create Cluster"
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default AddAnchorKeywordModal;
